import type { UserDocument } from "@/api/models"

import { isArtifactType } from "../artifact-types"
import { openArtifactInPanel } from "./open-artifact-panel"
import { useArtifactStore } from "../stores/artifact-store"

/** Safely reads persisted session image filenames from a document's artifactMeta. */
function readSessionFilenames(meta: Record<string, unknown> | undefined): string[] | undefined {
  const raw = meta?.sessionFilenames
  if (Array.isArray(raw) && raw.every((f) => typeof f === "string")) return raw as string[]
  return undefined
}

/**
 * Loads a saved (non-markdown) document into the artifact store as a synthetic,
 * complete artifact and opens it in the ArtifactPanel. Bypasses the streaming
 * tag-parsing path so a persisted document is never merged into session state.
 * An unknown persisted `artifactType` is coerced to "markdown" so a corrupt or
 * future type renders as code instead of crashing the panel. The session image
 * context (sourceSessionId + artifactMeta.sessionFilenames) is restored so
 * sandbox:/mnt/data/… references in html/react/slides still resolve.
 */
export function openSavedArtifact(doc: UserDocument): void {
  const type = isArtifactType(doc.artifactType) ? doc.artifactType : "markdown"
  useArtifactStore.getState().loadArtifact({
    identifier: doc.id,
    type,
    title: doc.title,
    content: doc.content || "",
    sessionId: doc.sourceSessionId ?? undefined,
    filenames: readSessionFilenames(doc.artifactMeta),
    // Saved artifacts serve their images from document-scoped storage (copied on
    // save), so they resolve for any participant regardless of chat access.
    imageSource: { kind: "document", documentId: doc.id },
  })
  const entry = useArtifactStore.getState().artifacts[doc.id]
  openArtifactInPanel(entry)
}
